import { IModalProps } from 'models/Modals'
import { Button, MuiModal } from '@pinchenterprisesnpm/friday-ui'
import { useModalsManager } from './useModalsManager'

interface IModalInstanceProps {
  modal: IModalProps
}

/**
 * ModalInstance renders a single modal based on its configuration from Redux state.
 *
 * Responsibilities:
 * - Render modal UI using provided props like title, content, buttons, and layout.
 * - Manage user interactions (confirm, cancel, close) with async-safe handlers.
 * - Control loading state during async confirm actions to prevent duplicate submissions.
 * - Delegate modal lifecycle events (close, kill) to the centralized `useModalsManager` hook,
 *   which handles triggering appropriate lifecycle callbacks (`onClose`, `onExited`).
 *
 * Props & Callbacks:
 * - `identifier`: Unique modal id used for dispatching modal actions.
 * - `title`, `content`: Modal UI elements.
 * - `isOpen`: Controls modal visibility.
 * - `onClose`, `onCancel`, `onConfirm`, `onExited`: Lifecycle and action callbacks for custom behavior.
 * - `isLoading`: Loading state flag to disable buttons and show spinners during async operations.
 * - Button labels and styles are customizable via props to support different modal use cases.
 *
 * This component focuses solely on rendering and user interaction.
 * All modal state and business logic should be managed outside via Redux and `useModalsManager`.
 */
export const ModalInstance = ({ modal }: IModalInstanceProps) => {
  const {
    identifier,
    title,
    content,
    isOpen = false,
    isLoading = false,
    onClose,
    onConfirm,
    onCancel,
    onExited,
    fullWidth = true,
    maxWidth = 'xs',
    cancelButtonLabel = 'Cancel',
    cancelButtonType = 'dark',
    cancelFilledOutlined = true,
    showCancelButton = true,
    confirmButtonLabel = 'Confirm',
    confirmButtonType = 'primary',
    confirmFilledOutlined = false,
    showConfirmButton = true,
  } = modal

  const { closeModal, killModal, refreshModalConfig } = useModalsManager()

  // Called when modal transition has exited
  const handleExited = () => {
    killModal(identifier) // will run onExited internally
  }

  // Called when modal close is triggered (e.g. escape key or X button is clicked)
  const handleClose = () => {
    closeModal(identifier) // will run onClose internally
  }

  /**
   * Handler for the Cancel button click.
   *
   * Invokes the modal's `onCancel` callback if defined,
   * then triggers modal closure via `closeModal` from the manager.
   */ const handleCancel = () => {
    onCancel?.()
    closeModal(identifier)
  }

  /**
   * Handler for the Confirm button click.
   *
   * Runs the modal-specific async `onConfirm` callback safely,
   * updating the modal's loading state via `refreshModalConfig` before and after.
   * Does NOT auto-close the modal; modal closure should be managed inside `onConfirm`.
   */ const handleConfirm = async () => {
    if (!onConfirm) return

    refreshModalConfig(identifier, { isLoading: true })

    try {
      await onConfirm()
    } catch (e) {
      // Errors are handled by the onConfirm callback (e.g., showing toast)
    } finally {
      refreshModalConfig(identifier, { isLoading: false })
    }
  }

  return (
    <MuiModal
      isOpen={isOpen}
      closeModal={handleClose}
      onExited={handleExited}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
    >
      <MuiModal.Header title={title} />
      <MuiModal.Content>{content}</MuiModal.Content>
      {(showCancelButton || showConfirmButton) && (
        <MuiModal.Footer>
          {showCancelButton && (
            <Button
              type={cancelButtonType}
              isFilledOutlined={cancelFilledOutlined}
              onClick={handleCancel}
              isDisabled={isLoading}
            >
              {cancelButtonLabel}
            </Button>
          )}
          {showConfirmButton && (
            <Button
              type={confirmButtonType}
              isFilledOutlined={confirmFilledOutlined}
              onClick={handleConfirm}
              isDisabled={isLoading}
              isLoading={isLoading}
            >
              {confirmButtonLabel}
            </Button>
          )}
        </MuiModal.Footer>
      )}
    </MuiModal>
  )
}
